import { useCallback, useMemo } from 'react';
import { Clock, Folder, History, type LucideIcon } from 'lucide-react';
import { useLocalStorageState } from '@reflex/ui/hooks/useWebStorageState';
import { parseSessionSort, useSessionSort, type SessionSort } from './useSessionSort';

/**
 * How the agent sidebar arranges its sessions, and how much of each row it
 * shows. Both are per-browser presentation preferences: every view lists the
 * same sessions.
 *
 * The three views are one choice to the user but two stored settings
 * underneath. `activity` / `created` are the existing {@link SessionSort},
 * shared with the settings page; `groups` is a different *shape* — sessions
 * bucketed by the group they were filed into rather than by time — so it
 * rides on its own flag and leaves the sort alone. That way switching to
 * Groups and back returns you to the order you had, and the sort still
 * decides the order inside each bucket.
 *
 * Labels deliberately have no view of their own: an agent carries several of
 * them, so bucketing by label either duplicates rows or picks one arbitrarily.
 * The label filter in the filter bar answers the same question exactly once.
 */
export const SESSION_VIEW_GROUPS = 'groups';
/** The one shape that buckets by membership rather than by time. */
export type SessionGroupingView = typeof SESSION_VIEW_GROUPS;
export type SessionView = SessionSort | SessionGroupingView;

const GROUPING_STORAGE_KEY = 'agent-session-grouping';
const ROW_FIELDS_STORAGE_KEY = 'agent-row-fields';

export interface SessionViewOption {
  value: SessionView;
  label: string;
  description: string;
  icon: LucideIcon;
}

/** The views offered in the sidebar's view menu, in presentation order. */
export const SESSION_VIEW_OPTIONS: readonly SessionViewOption[] = [
  {
    value: 'activity',
    label: 'Recent activity',
    description: 'Most recently active first.',
    icon: History,
  },
  {
    value: 'created',
    label: 'Date created',
    description: 'Newest first.',
    icon: Clock,
  },
  {
    value: SESSION_VIEW_GROUPS,
    label: 'Groups',
    description: 'Bucketed by the group each session is in.',
    icon: Folder,
  },
];

/**
 * The active view plus a setter that writes whichever of the two underlying
 * settings the choice implies. Picking a time-based view also leaves the
 * groups flag off, so the two can never both claim the list.
 */
export function useSessionView(): [SessionView, (view: SessionView) => void] {
  const [sort, setSort] = useSessionSort();
  const [rawGrouping, setRawGrouping] = useLocalStorageState<string | boolean>(
    GROUPING_STORAGE_KEY,
    '',
  );
  // `true` is the value written when grouping was a boolean "bucket by group"
  // flag. Read it as the group view rather than dropping people back into a
  // time view on upgrade. Anything else — including the `labels` value written
  // while the label view existed — reads as no grouping, so a browser left in
  // that view lands back on the sort it had.
  const grouping: SessionGroupingView | null =
    rawGrouping === true || rawGrouping === SESSION_VIEW_GROUPS ? SESSION_VIEW_GROUPS : null;

  const setView = useCallback(
    (view: SessionView) => {
      if (view === SESSION_VIEW_GROUPS) {
        setRawGrouping(view);
        return;
      }
      setRawGrouping('');
      setSort(parseSessionSort(view));
    },
    [setRawGrouping, setSort],
  );

  return [grouping ?? sort, setView];
}

/** Whether the sidebar is bucketing by group right now. */
export function useSessionGrouping(): SessionGroupingView | null {
  const [view] = useSessionView();
  return view === SESSION_VIEW_GROUPS ? view : null;
}

/**
 * Which pieces of the row meta line to show. All on by default: the line is
 * the row's context, and hiding it is an explicit choice for people who want
 * a denser list (or who run one agent type against one repo, where the
 * segments are the same on every row and carry no information).
 */
export interface AgentRowFields {
  agentType: boolean;
  model: boolean;
  repo: boolean;
}

export const AGENT_ROW_FIELD_LABELS: Record<keyof AgentRowFields, string> = {
  agentType: 'Agent',
  model: 'Model',
  repo: 'Repo',
};

export const AGENT_ROW_FIELD_KEYS = Object.keys(AGENT_ROW_FIELD_LABELS) as (keyof AgentRowFields)[];

const ALL_FIELDS_ON: AgentRowFields = { agentType: true, model: true, repo: true };

/**
 * Parse the stored value: anything missing or malformed reads as "shown", so
 * a stale key from an older build can never blank out a row.
 */
function parseRowFields(stored: unknown): AgentRowFields {
  if (!stored || typeof stored !== 'object') return ALL_FIELDS_ON;
  const record = stored as Record<string, unknown>;
  return {
    agentType: record.agentType !== false,
    model: record.model !== false,
    repo: record.repo !== false,
  };
}

export interface AgentRowFieldsResult {
  fields: AgentRowFields;
  toggleField: (field: keyof AgentRowFields) => void;
}

/** The row meta-line preferences, reactive across every row and tab. */
export function useAgentRowFields(): AgentRowFieldsResult {
  const [raw, setRaw] = useLocalStorageState<Partial<AgentRowFields>>(
    ROW_FIELDS_STORAGE_KEY,
    ALL_FIELDS_ON,
  );
  const fields = useMemo(() => parseRowFields(raw), [raw]);
  const toggleField = useCallback(
    (field: keyof AgentRowFields) => setRaw({ ...fields, [field]: !fields[field] }),
    [fields, setRaw],
  );
  return { fields, toggleField };
}
